import { loadAppConfig } from "./appConfig.ts";

/**
 * Google Identity Services betiğinin yüklenmesi ve yapılandırma.
 *
 * İstemci kimliği derleme anında gömülmüyor, sunucudan çalışma anında geliyor:
 * Docker imajı build argümanı almıyor, ayarı Railway'de değiştirip yeniden
 * dağıtmak yetsin istiyoruz. Kimlik tanımlı değilse düğme hiç gösterilmez.
 */

const SCRIPT_SRC = "https://accounts.google.com/gsi/client";

export interface GoogleIdConfiguration {
  client_id: string;
  callback: (response: { credential?: string }) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

export interface GoogleButtonOptions {
  type?: "standard" | "icon";
  theme?: "outline" | "filled_blue" | "filled_black";
  size?: "small" | "medium" | "large";
  text?: "signin_with" | "signup_with" | "continue_with";
  shape?: "rectangular" | "pill";
  width?: number;
  locale?: string;
  logo_alignment?: "left" | "center";
}

interface GoogleAccountsId {
  initialize(config: GoogleIdConfiguration): void;
  renderButton(parent: HTMLElement, options: GoogleButtonOptions): void;
  disableAutoSelect(): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

/** Sunucudaki Google istemci kimliği; özellik kapalıysa null. */
export function loadGoogleClientId(): Promise<string | null> {
  return loadAppConfig().then((config) => config.googleClientId);
}

let scriptPromise: Promise<GoogleAccountsId | null> | null = null;

/** GIS betiğini bir kez yükler. Çevrimdışıysa null döner, ekran çalışmaya devam eder. */
export function loadGoogleIdentity(): Promise<GoogleAccountsId | null> {
  scriptPromise ??= new Promise<GoogleAccountsId | null>((resolve) => {
    if (window.google?.accounts?.id) {
      resolve(window.google.accounts.id);
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    const script = existing ?? document.createElement("script");
    const done = () => resolve(window.google?.accounts?.id ?? null);

    script.addEventListener("load", done, { once: true });
    script.addEventListener("error", () => resolve(null), { once: true });

    if (!existing) {
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  return scriptPromise;
}
